/**
 * Active sync — keep integration data fresh while the user is actually using
 * the app, instead of relying solely on the 15-minute server cron (which can
 * lag for hours and leaves freshly-opened sessions staring at stale data).
 *
 * Strategy (client-driven, cron stays the offline safety net):
 *   1. On sign-in / app open — kick a sync so the first screen is current.
 *   2. On tab refocus — when the user comes back after being away.
 *   3. Heartbeat — every few minutes while the tab stays visible.
 *
 * All three call POST /v1/integrations/sync-all with `staleMinutes`, so the
 * server only re-hits providers that are actually stale — the call is cheap
 * and won't hammer provider APIs or burn quota. A debounce + in-flight guard
 * + a shared localStorage timestamp keep multiple tabs and focus storms from
 * triggering redundant syncs.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { supabase } from '../supabase';

/** Re-check freshness this often while the tab is open and visible. */
const HEARTBEAT_MS = 5 * 60_000;
/** Never fire two syncs within this window (covers multi-tab + refocus storms). */
const DEBOUNCE_MS = 2 * 60_000;
/** Server only re-syncs providers whose last sync is older than this. */
const STALE_MINUTES = 10;
/** Shared across tabs so only one tab syncs per debounce window. */
const LS_KEY = 'waypoint:last-active-sync';

function lastRunAt(): number {
  try { return Number(localStorage.getItem(LS_KEY) ?? 0); } catch { return 0; }
}
function markRun(): void {
  try { localStorage.setItem(LS_KEY, String(Date.now())); } catch { /* private mode */ }
}

export function useActiveSync(): void {
  const queryClient = useQueryClient();
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function maybeSync(): Promise<void> {
      if (cancelled || inFlight.current) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (Date.now() - lastRunAt() < DEBOUNCE_MS) return;

      // Only sync when actually signed in — getSession is cheap (cached).
      const { data } = await supabase.auth.getSession();
      if (!data.session || cancelled) return;

      inFlight.current = true;
      markRun(); // mark before awaiting so a second tab/event doesn't double-fire
      try {
        await api('/v1/integrations/sync-all', {
          method: 'POST',
          body: JSON.stringify({ staleMinutes: STALE_MINUTES }),
        });
        if (!cancelled) {
          // Refresh the integration cards + any data the synced providers feed.
          queryClient.invalidateQueries({ queryKey: ['integrations'] });
        }
      } catch {
        // Best-effort: the server cron remains the safety net. Stay quiet so a
        // transient failure never interrupts the user with a toast.
      } finally {
        inFlight.current = false;
      }
    }

    // 1. App opened / signed in.
    void maybeSync();

    // 2. Tab regained focus after being hidden.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void maybeSync();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // 3. Heartbeat while the tab stays open.
    const interval = window.setInterval(() => { void maybeSync(); }, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [queryClient]);
}
